import { useEffect, useRef, type CSSProperties } from "react";
import { createPortal } from "react-dom";
import { Link2, Upload, PenLine, ArrowRight, X, type LucideIcon } from "lucide-react";
import type { SetupOption } from "../overviewTypes";

/**
 * The "Set up accounting" modal. Shown from both the empty state
 * CTA and the Overview header "Set up" button. Fires onSelect
 * with the chosen option. The parent decides what to do next
 * (navigate, open a drawer, show a toast).
 *
 * Portal-rendered to document.body so it escapes AppLayout stacking.
 */

interface SetupAccountingModalProps {
  open: boolean;
  onClose: () => void;
  onSelect: (option: SetupOption) => void;
}

interface OptionDef {
  key: SetupOption;
  icon: LucideIcon;
  title: string;
  description: string;
}

const PALETTE = {
  ink: "#0E1A1A",
  ink2: "#263238",
  ink3: "#3A4A52",
  ink4: "#6B7684",
  lineSoft: "#E7EAF0",
  bgFade: "#F3F5F5",
};

const OPTIONS: OptionDef[] = [
  {
    key: "connect_bank",
    icon: Link2,
    title: "Connect a bank",
    description:
      "Pull in transactions automatically from your Canadian bank or credit union.",
  },
  {
    key: "upload_csv",
    icon: Upload,
    title: "Upload a CSV statement",
    description: "Drop a file from your online banking. Works with any bank.",
  },
  {
    key: "enter_manually",
    icon: PenLine,
    title: "Enter transactions manually",
    description: "Record each transaction yourself. Great for petty cash.",
  },
];

export function SetupAccountingModal({
  open,
  onClose,
  onSelect,
}: SetupAccountingModalProps) {
  const closeBtnRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!open) return;

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);

    closeBtnRef.current?.focus();

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  const content = (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="setup-accounting-title"
      style={overlayStyle}
      onClick={onClose}
    >
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <div style={headerStyle}>
          <div>
            <h2 id="setup-accounting-title" style={titleStyle}>
              Set up accounting
            </h2>
            <p style={subtitleStyle}>
              How would you like to add your transactions?
            </p>
          </div>
          <button
            ref={closeBtnRef}
            type="button"
            aria-label="Close"
            style={closeBtnStyle}
            onClick={onClose}
          >
            <X size={18} color={PALETTE.ink3} strokeWidth={2} />
          </button>
        </div>

        <div style={optionsListStyle}>
          {OPTIONS.map((opt, i) => {
            const Icon = opt.icon;
            return (
              <button
                key={opt.key}
                type="button"
                style={{
                  ...optionRowStyle,
                  borderTop:
                    i === 0 ? "none" : `1px solid ${PALETTE.lineSoft}`,
                }}
                onClick={() => onSelect(opt.key)}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.background =
                    PALETTE.bgFade;
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.background =
                    "transparent";
                }}
              >
                <div style={optionIconWrapStyle}>
                  <Icon size={20} color={PALETTE.ink2} strokeWidth={1.75} />
                </div>
                <div style={optionTextWrapStyle}>
                  <div style={optionTitleStyle}>{opt.title}</div>
                  <div style={optionDescStyle}>{opt.description}</div>
                </div>
                <ArrowRight size={16} color={PALETTE.ink4} strokeWidth={2} />
              </button>
            );
          })}
        </div>

        <div style={footerStyle}>
          <p style={footerTextStyle}>
            You can mix and match later. Add more accounts anytime from Chart
            of accounts.
          </p>
        </div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
}

const overlayStyle: CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(14, 26, 26, 0.52)",
  display: "flex",
  justifyContent: "center",
  alignItems: "flex-start",
  padding: "80px 20px 20px",
  zIndex: 1000,
};

const modalStyle: CSSProperties = {
  background: "#ffffff",
  borderRadius: 12,
  width: "100%",
  maxWidth: 540,
  boxShadow:
    "0 24px 48px -12px rgba(14, 26, 26, 0.28), 0 8px 20px -4px rgba(14, 26, 26, 0.12)",
  overflow: "hidden",
};

const headerStyle: CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  padding: "22px 24px 18px",
  borderBottom: `1px solid ${PALETTE.lineSoft}`,
};

const titleStyle: CSSProperties = {
  fontSize: 18,
  fontWeight: 700,
  color: PALETTE.ink,
  margin: 0,
  letterSpacing: "-0.01em",
};

const subtitleStyle: CSSProperties = {
  fontSize: 13,
  color: PALETTE.ink3,
  margin: "4px 0 0",
};

const closeBtnStyle: CSSProperties = {
  background: "transparent",
  border: 0,
  cursor: "pointer",
  padding: 6,
  borderRadius: 6,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
};

const optionsListStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
};

const optionRowStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 14,
  padding: "18px 24px",
  background: "transparent",
  border: 0,
  textAlign: "left",
  cursor: "pointer",
  fontFamily: "inherit",
  transition: "background 120ms ease",
  width: "100%",
};

const optionIconWrapStyle: CSSProperties = {
  width: 36,
  height: 36,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
};

const optionTextWrapStyle: CSSProperties = {
  flex: 1,
  display: "flex",
  flexDirection: "column",
  gap: 2,
};

const optionTitleStyle: CSSProperties = {
  fontSize: 14,
  fontWeight: 600,
  color: PALETTE.ink,
  lineHeight: 1.4,
};

const optionDescStyle: CSSProperties = {
  fontSize: 12.5,
  color: PALETTE.ink3,
  lineHeight: 1.5,
};

const footerStyle: CSSProperties = {
  padding: "14px 24px 18px",
  background: PALETTE.bgFade,
  borderTop: `1px solid ${PALETTE.lineSoft}`,
};

const footerTextStyle: CSSProperties = {
  fontSize: 12,
  color: PALETTE.ink4,
  margin: 0,
  lineHeight: 1.5,
};